import {inject, Injectable, NgZone, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {captureException} from '@sentry/angular';
import {ScriptService} from '@core/services/script.service';
import {CookieService} from '@core/services/cookie.service';
import {environment} from '@env/environment';

/**
 * Service responsible for lazy loading Cookiebot script.
 * Only loads Cookiebot on first user interaction (scroll, click, mousemove)
 * unless consent cookie already exists.
 */
@Injectable({
  providedIn: 'root'
})
export class CookiebotLoaderService {
  private ngZone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);
  private scriptService = inject(ScriptService);
  private cookieService = inject(CookieService);

  private cookiebotLoaded = false;
  private interactionListenersAdded = false;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.initLazyLoading();
    }
  }

  private initLazyLoading(): void {
    // Check if consent cookie already exists
    const consentCookie = this.cookieService.get('CookieConsent');
    
    if (consentCookie) {
      // User has already given consent, load Cookiebot immediately
      this.loadCookiebot();
    } else {
      // No consent yet, wait for first user interaction
      this.addInteractionListeners();
    }
  }

  private addInteractionListeners(): void {
    if (this.interactionListenersAdded) {
      return;
    }

    this.interactionListenersAdded = true;

    this.ngZone.runOutsideAngular(() => {
      // Events that indicate user interaction
      const events = ['scroll', 'click', 'mousemove', 'touchstart', 'keydown'];
      
      const loadOnInteraction = () => {
        if (!this.cookiebotLoaded) {
          this.loadCookiebot();
          // Remove event listeners after first interaction
          events.forEach(event => {
            window.removeEventListener(event, loadOnInteraction, {capture: true});
          });
        }
      };

      // Add listeners for all interaction events
      events.forEach(event => {
        window.addEventListener(event, loadOnInteraction, {capture: true, passive: true});
      });
    });
  }

  private loadCookiebot(): void {
    if (this.cookiebotLoaded) {
      return;
    }

    this.cookiebotLoaded = true;

    this.scriptService.addScript({
      type: 'text/javascript',
      src: 'https://consent.cookiebot.com/uc.js',
      data: {
        'cbid': environment.cookiebot.id,
        'blockingmode': 'auto'
      },
      async: true,
      id: 'Cookiebot'
    }, false, 'head').then(() => {
      const cookiebot = (window as any)?.Cookiebot;
      if (typeof cookiebot?.signalWindowLoad === 'function') {
        cookiebot.signalWindowLoad();
      }
    }).catch(error => {
      console.error('Failed to load or initialize Cookiebot:', error);
      captureException(error);
    });
  }

  /**
   * Force load Cookiebot immediately.
   * Useful for manual consent management.
   */
  public forceLoad(): void {
    this.loadCookiebot();
  }

  /**
   * Open Cookiebot consent dialog.
   * Ensures Cookiebot is loaded before attempting to open the dialog.
   * Removes existing dialog elements before calling renew() to force
   * Cookiebot to rebuild the dialog (fixes re-open after Reject/Allow selection).
   */
  public openConsentDialog(): void {
    this.forceLoad();

    const openDialog = () => {
      const cookiebot = (window as any)?.Cookiebot;
      if (cookiebot?.renew) {
        // Remove existing dialog elements to force Cookiebot to recreate them.
        // Without this, renew() silently fails after "Reject" or "Allow selection".
        document.getElementById('CybotCookiebotDialog')?.remove();
        document.getElementById('CybotCookiebotDialogBodyUnderlay')?.remove();
        cookiebot.renew();
      } else {
        // Retry after a short delay if Cookiebot isn't ready yet
        setTimeout(openDialog, 100);
      }
    };

    setTimeout(openDialog, 50);
  }

  /**
   * Check if Cookiebot is loaded
   */
  public isLoaded(): boolean {
    return this.cookiebotLoaded;
  }
}
